import { useTheme } from '@approvalmax/theme';
import { Spacing } from '@approvalmax/ui/src/components';
import { browserHelpers, errorHelpers } from '@approvalmax/utils';
import { constants, selectors } from 'modules/common';
import {
    completeFirstStartSetup,
    updateAcceptedInvitations,
    updatePracticeAcceptedInvitations,
    updateProfileData,
} from 'modules/first-start/actions';
import { ProfileData } from 'modules/first-start/reducers/moduleReducer';
import {
    getAcceptedInvitationIds,
    getLoading,
    getProfileData,
    getSelectedPracticeInvitation,
} from 'modules/first-start/selectors/moduleSelectors';
import { useDispatch, useSelector } from 'modules/react-redux';
import {
    AcceptInvitationsStep,
    MobileSuccessInfo,
    PendingPracticeInvitationStep,
    ProfileStepContainer,
} from 'pages/auth/pages/FirstTimeSetupPage/components';
import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useMedia } from 'react-use';
import { amplitudeService } from 'services/amplitude';
import { gaService } from 'services/ga';
import { routingService } from 'services/routing';
import { useGetInvitations } from 'shared/data/webApp/v2';
import { getPath, Path } from 'urlBuilder';

import MobileLogo from '../MobileLogo/MobileLogo';
import { messages } from './SetupProfileWizard.messages';
import { Body, NextButton, Root, Title } from './SetupProfileWizard.styles';
import { FirstStartWizardStep } from './SetupProfileWizard.types';

const SetupProfileWizard: FC = memo(() => {
    const startupData = useSelector(selectors.session.getStartupData);
    const { data } = useGetInvitations();
    const invitations = useMemo(() => data?.items || [], [data?.items]);
    const practiceInvitations = useSelector(selectors.practiceInvitations.getPracticeInvitations);
    const profileData = useSelector(getProfileData);
    const profile = useSelector(selectors.profile.findProfile);
    const acceptedInvitationIds = useSelector(getAcceptedInvitationIds);
    const selectedPracticeInvitation = useSelector(getSelectedPracticeInvitation);
    const loading = useSelector(getLoading);

    const dispatch = useDispatch();
    const onAcceptedInvitationsChange = useCallback(
        (acceptedInvitationIds: string[]) => {
            dispatch(updateAcceptedInvitations(acceptedInvitationIds));
        },
        [dispatch]
    );
    const onAcceptedPracticeInvitationsChange = useCallback(
        (option: string) => {
            dispatch(updatePracticeAcceptedInvitations(option));
        },
        [dispatch]
    );
    const onProfileDataChange = useCallback(
        (profileData: ProfileData) => {
            dispatch(updateProfileData(profileData));
        },
        [dispatch]
    );

    const noInvitations = invitations.length === 0;

    useEffect(() => {
        amplitudeService.sendData('signup: land on user profile setup screen', {
            'signup type': 'email',
        });

        if (noInvitations) {
            gaService.sendEvent('land_on_user_profile', {
                method: 'direct',
            });
        }
    }, [noInvitations]);

    const { theme } = useTheme();
    const isMobileScreen = useMedia(`(max-width: ${theme.container.breakpoint.xsmall})`);
    const isMobile = browserHelpers.isMobile();

    const onCompleteFirstStartSetup = useCallback(async () => {
        await dispatch(completeFirstStartSetup({ isMobile, invitations }));

        if (isMobile) {
            if (invitations.length === 0 && practiceInvitations.length === 0) {
                routingService.push(getPath(Path.completeSetup));
            }

            setCurrentStep(FirstStartWizardStep.MobileSuccessInfo);
        }
    }, [dispatch, invitations, isMobile, practiceInvitations.length]);

    const [currentStep, setCurrentStep] = useState(() => {
        if (startupData.needsToCompleteSignupWizard) {
            return FirstStartWizardStep.Profile;
        } else if (invitations.length) {
            return FirstStartWizardStep.AcceptInvitations;
        } else if (practiceInvitations.length) {
            return FirstStartWizardStep.AcceptPracticeInvitations;
        }

        return FirstStartWizardStep.MobileSuccessInfo;
    });

    const stepInfo = useMemo(() => {
        let canNext;
        let nextStep;
        let stepTitle;

        switch (currentStep) {
            case FirstStartWizardStep.Profile:
                if (invitations.length) {
                    nextStep = FirstStartWizardStep.AcceptInvitations;
                } else if (practiceInvitations.length) {
                    nextStep = FirstStartWizardStep.AcceptPracticeInvitations;
                } else {
                    nextStep = null;
                }

                stepTitle = messages.stepProfileTitle;
                canNext = profileData.isValid;
                break;

            case FirstStartWizardStep.AcceptInvitations: {
                canNext = true;
                stepTitle = isMobileScreen ? messages.stepInvitationsTitleMobile : messages.stepInvitationsTitle;

                if (practiceInvitations.length) {
                    nextStep = FirstStartWizardStep.AcceptPracticeInvitations;
                } else {
                    nextStep = null;
                }

                break;
            }

            case FirstStartWizardStep.AcceptPracticeInvitations: {
                nextStep = null;
                canNext = true;

                stepTitle = messages.practiceInvitationspopupTitle;
                break;
            }

            case FirstStartWizardStep.MobileSuccessInfo: {
                nextStep = null;
                canNext = false;
                stepTitle = '';
                break;
            }

            default:
                throw errorHelpers.invalidOperationError();
        }

        return {
            nextStep,
            canNext,
            stepTitle,
        };
    }, [currentStep, invitations.length, isMobileScreen, practiceInvitations.length, profileData.isValid]);

    const goNext = useCallback(() => {
        if (currentStep === FirstStartWizardStep.AcceptPracticeInvitations) {
            const acceptanceType =
                selectedPracticeInvitation === constants.commonConstants.DECLINE_ALL_INVITATIONS_OPTION
                    ? 'declined'
                    : 'accepted';

            amplitudeService.sendData('signup: decide on business account invitations', {
                decision: acceptanceType,
            });
        }

        if (currentStep === FirstStartWizardStep.AcceptInvitations) {
            const acceptanceType = acceptedInvitationIds.length ? 'accepted' : 'declined';

            amplitudeService.sendData('signup: decide on org invitations', {
                decision: acceptanceType,
            });
        }

        if (currentStep === FirstStartWizardStep.Profile) {
            onProfileDataChange({
                ...profileData,
                firstName: profileData.firstName.trim(),
                lastName: profileData.lastName.trim(),
            });
        }

        if (!stepInfo.canNext) {
            // Current step is incomplete
            return;
        }

        if (stepInfo.nextStep) {
            // Go to the next step
            setCurrentStep(stepInfo.nextStep);

            return;
        }

        // Finish action
        onCompleteFirstStartSetup();
    }, [
        acceptedInvitationIds.length,
        currentStep,
        onCompleteFirstStartSetup,
        onProfileDataChange,
        profileData,
        selectedPracticeInvitation,
        stepInfo.canNext,
        stepInfo.nextStep,
    ]);

    let bodyComponent;

    switch (currentStep) {
        case FirstStartWizardStep.Profile:
            return (
                <Root>
                    {isMobileScreen && (
                        <>
                            <Spacing height={40} />

                            <MobileLogo loading={loading} />
                        </>
                    )}

                    <Body>
                        <ProfileStepContainer
                            profileData={profileData}
                            hasInvitations={invitations.length > 0}
                            email={profile?.email}
                            nextStep={goNext}
                        />
                    </Body>
                </Root>
            );

            break;

        case FirstStartWizardStep.AcceptInvitations:
            bodyComponent = (
                <AcceptInvitationsStep
                    invitations={invitations}
                    acceptedInvitationIds={acceptedInvitationIds}
                    onAcceptedChange={onAcceptedInvitationsChange}
                    onNext={goNext}
                />
            );
            break;

        case FirstStartWizardStep.AcceptPracticeInvitations:
            bodyComponent = (
                <PendingPracticeInvitationStep
                    value={selectedPracticeInvitation}
                    invitations={practiceInvitations}
                    onSelectOption={onAcceptedPracticeInvitationsChange}
                />
            );
            break;

        case FirstStartWizardStep.MobileSuccessInfo:
            return (
                <Root>
                    <Body>
                        <MobileSuccessInfo />
                    </Body>
                </Root>
            );

            break;

        default:
            throw errorHelpers.invalidOperationError();
    }

    return (
        <Root>
            {isMobileScreen ? (
                <>
                    <Spacing height={40} />

                    <MobileLogo title={stepInfo.stepTitle} />
                </>
            ) : (
                <Title>{stepInfo.stepTitle}</Title>
            )}

            <Body $withPadding>
                {bodyComponent}

                <NextButton execute={goNext} disabled={loading} preset={isMobileScreen ? 'large' : 'normal'}>
                    {messages.nextButton}
                </NextButton>
            </Body>
        </Root>
    );
});

export default SetupProfileWizard;
